/**
 * <AdminGuard>
 * --------------------------------------------------------------
 * Onda 1B do hardening (junho/2026). Componente que protege rotas
 * administrativas (`/medico`, `/admin/health`, `/suporte`, etc.) com
 * base em Firebase Auth + custom claim `role`.
 *
 * Uso:
 *   <AdminGuard allowedRoles={["doctor","admin"]}>
 *     <DoctorDashboard />
 *   </AdminGuard>
 *
 * Estados:
 *   - loading: spinner
 *   - !user: mostra <AdminLogin />
 *   - user sem role permitida: tela "sem permissao"
 *   - user precisa MFA: redireciona para enrollment
 *   - tudo ok: renderiza children
 */

import React, { useEffect, useState, useCallback } from 'react';
import { observeAdmin, signOutAdmin, hasMfaEnrolled, roleHasAccess } from './adminAuth';
import AdminLogin from './AdminLogin';
import AdminMfaEnroll from './AdminMfaEnroll';

const REQUIRE_MFA = (process.env.REACT_APP_ADMIN_REQUIRE_MFA || 'true').toLowerCase() !== 'false';

export default function AdminGuard({ allowedRoles, children, title }) {
  const [state, setState] = useState({ ready: false, user: null, role: null, claims: {} });
  const [needsMfaEnroll, setNeedsMfaEnroll] = useState(false);

  useEffect(() => {
    const unsub = observeAdmin((s) => {
      setState({ ready: s.ready, user: s.user, role: s.role, claims: s.claims || {} });
      if (s.user) {
        const enrolled = hasMfaEnrolled(s.user);
        setNeedsMfaEnroll(REQUIRE_MFA && !enrolled);
      } else {
        setNeedsMfaEnroll(false);
      }
    });
    return unsub;
  }, []);

  const handleLogout = useCallback(async () => {
    await signOutAdmin();
  }, []);

  if (!state.ready) {
    return (
      <div style={fullScreen}>
        <div style={card}>
          <p style={{ color: '#6b7280' }}>Carregando…</p>
        </div>
      </div>
    );
  }

  if (!state.user) {
    return <AdminLogin title={title} />;
  }

  // MFA obrigatorio em paineis admin (Onda 1B).
  if (needsMfaEnroll) {
    return (
      <AdminMfaEnroll
        user={state.user}
        onDone={() => setNeedsMfaEnroll(false)}
        onCancel={handleLogout}
      />
    );
  }

  if (!roleHasAccess(state.role, allowedRoles)) {
    return (
      <div style={fullScreen}>
        <div style={card}>
          <h2 style={{ marginTop: 0 }}>Acesso negado</h2>
          <p>
            Esta área é restrita aos perfis: <strong>{(allowedRoles || []).join(', ')}</strong>.
          </p>
          <p style={{ color: '#6b7280', fontSize: 13 }}>
            Logado como <strong>{state.user.email}</strong>{' '}
            (role: <strong>{state.role || 'nenhuma'}</strong>).
          </p>
          <button type="button" onClick={handleLogout} style={btnSecondary}>
            Sair
          </button>
        </div>
      </div>
    );
  }

  return children;
}

const fullScreen = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f4f4f5',
  padding: 16,
  fontFamily: "'Segoe UI', Roboto, sans-serif",
};

const card = {
  background: '#fff',
  borderRadius: 12,
  padding: '32px 28px',
  maxWidth: 420,
  width: '100%',
  boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
};

const btnSecondary = {
  marginTop: 18,
  padding: '10px 18px',
  background: '#fff',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  cursor: 'pointer',
};
